import React, { useEffect, useState, useRef, useCallback } from "react";
import { withStyles } from "@mui/styles";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { Autocomplete, Chip, CircularProgress, FormControl, TextField, Grid, RadioGroup, useRadioGroup, styled, FormControlLabel, Radio } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import FieldNames from "../../../../defaults/FieldNames.json";
import UserHelperService from "../../../../services/UserHelperService";
import ErrorService from "../../../../services/ErrorService";
import ColorConstants from "../../../../defaults/ColorConstants";

const theme = createTheme({
  overrides: {
    palette: {
      red: {
        main: "#FF0000",
        light: "#FF0000",
        dark: "#FF0000",
        contrastText: "#242105",
      },
    },
    MuiPaper: {
      root: {
        backgroundColor: "green",
      },
    },
    MuiOutlinedInput: {
      root: {
        "& $notchedOutline": {
          //initial border
          borderColor: ColorConstants.BlueAlien,
        },
        "&:hover $notchedOutline": {
          //border on hover
          borderColor: ColorConstants.BlueAlien,
          border: "2px solid",
        },
        "&$focused $notchedOutline": {
          //border on focus
          borderColor: ColorConstants.BlueAlien,
          border: "2px solid",
        },
      },
    },
  },
});

const StyledFormControlLabel = styled((props) => (
  <FormControlLabel {...props} />
))(({ theme, checked }) => ({
  ".MuiFormControlLabel-label": {
    // Change color here
    color: ColorConstants.Default
  },
  ".MuiFormControlLabel": {
    // Change color here
    color: ColorConstants.Default
  }
}));

const HeaderRadio = withStyles({
  root: {
    color: ColorConstants.BlueAlien,
    padding: 0,
    '&$checked': {
      color: ColorConstants.BlueAlien,
    },
  },
  checked: {},
})((props) => <Radio color="default" {...props} />);

function HeaderFormControlLabel(props) {
  // MUI UseRadio Group
  const radioGroup = useRadioGroup();

  let checked = false;

  if (radioGroup) {
    checked = radioGroup.value === props.value;
  }

  return <StyledFormControlLabel checked={checked} {...props} />;
}

export default function WorkflowGridFilters({
  currentUserInfo,
  userRoleInfo,
  fundingDepartmentSelection,
  setFundingDepartmentSelection,
  fundingAgencySelection,
  setFundingAgencySelection,
  fundingRegionSelection,
  setFundingRegionSelection,
  fundingUnitSelection,
  setFundingUnitSelection,
  fundingSubUnitSelection,
  setFundingSubUnitSelection,
  fundingTribeSelection,
  setFundingTribeSelection,
  entityTypeSelection,
  setEntityTypeSelection,
  setColumnVisibilityModel,
  setTotalCount,
  setIsTreatment = null,
  setClassOptionSelection = null,
}) {
  const rootServiceUrl = useSelector(
    (state) => state.configuration.rootServiceUrl
  );
  const config = useSelector((state) => state.configuration.config);
  const nifcToken = useSelector((state) => state.configuration.nifcToken);
  const [departmentOpen, setDepartmentOpen] = useState(false);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const departmentLoading = departmentOpen && departmentOptions.length === 0;
  const [agencyOpen, setAgencyOpen] = useState(false);
  const [agencyOptions, setAgencyOptions] = useState([]);
  const agencyLoading = agencyOpen && agencyOptions.length === 0;
  const [regionOpen, setRegionOpen] = useState(false);
  const [regionOptions, setRegionOptions] = useState([]);
  const regionLoading = regionOpen && regionOptions.length === 0;
  const [unitOpen, setUnitOpen] = useState(false);
  const [unitOptions, setUnitOptions] = useState([]);
  const unitLoading = unitOpen && unitOptions.length === 0;
  const [subUnitOpen, setSubUnitOpen] = useState(false);
  const [subUnitOptions, setSubUnitOptions] = useState([]);
  const [unitTypeOpen, setUnitTypeOpen] = useState(false);
  const [unitTypeOptions, setUnitTypeOptions] = useState([]);
  const unitTypeLoading = unitTypeOpen && unitTypeOptions.length === 0;
  const subUnitLoading = subUnitOpen && subUnitOptions.length === 0;
  const [tribeOpen, setTribeOpen] = useState(false);
  const [tribeOptions, setTribeOptions] = useState([]);
  const isUserFuelsAndPostFire = (currentUserInfo[FieldNames.userFields.isPostFire] === FieldNames.yesNo.yes && currentUserInfo[FieldNames.userFields.isFuels] === FieldNames.yesNo.yes) ? true : false;
  const isUserPostFire = currentUserInfo[FieldNames.userFields.isPostFire] === FieldNames.yesNo.yes ? true : false;
  const tribeLoading = tribeOpen && tribeOptions.length === 0;
  const showSubunit =
    fundingAgencySelection &&
    fundingAgencySelection[FieldNames.budgetFields.agency] === "BLM";
  const showTribe =
    fundingAgencySelection &&
    fundingAgencySelection[FieldNames.budgetFields.agency] === "BIA";
  
  const useStyles = makeStyles({
    underline: {
      "&&&:before": {
        borderBottom: "2px solid red",
      },
      "&&:after": {
        borderBottom: "2px solid red",
      },
    },
    condensedFormControl: {
      margin: "5px 15px 5px 15px",
      minWidth: 120,
      width: "90%",
    },
    formControl: {
      margin: "10px 15px 10px 15px",
      minWidth: 120,
      width: "90%",
    },
    formControlHeader: {
      margin: "0px 15px 0px 15px",
      minWidth: 120,
      width: "90%",
    },
    snackBarStyle: {
      top: "120px !important",
    },
  });
  const classes = useStyles();

  useEffect(() => {
    if(isUserFuelsAndPostFire){
      setEntityTypeSelection(FieldNames.datasets.fuels)
    } else {
      if(isUserPostFire){
        setEntityTypeSelection(FieldNames.datasets.postfire)
      } else {
        setEntityTypeSelection(FieldNames.datasets.fuels)
      }
    }
  }, [isUserFuelsAndPostFire, isUserPostFire, setEntityTypeSelection])
  
  useEffect(() => {
    //
    // Manage Dropdowns
    //
    let departments = [];
    userRoleInfo.forEach((item) => {
      if (
        item[FieldNames.budgetFields.department] &&
        departments &&
        !departments.includes(item[FieldNames.budgetFields.department])
      ) {
        departments.push(item);
      }
    });
    if (departments && departments.length > 0) {
      setFundingDepartmentSelection(departments[0]);
      setDepartmentOptions([departments[0]]);
    } else {
      setFundingDepartmentSelection(["Department of the Interior"]);
    }

    let agencies = [];
    userRoleInfo.forEach((item) => {
      if (
        item[FieldNames.budgetFields.agency] &&
        agencies &&
        !agencies.includes(item[FieldNames.budgetFields.agency])
      ) {
        agencies.push(item);
      }
    });
    if (agencies && agencies.length > 0) {
      setFundingAgencySelection(agencies[0]);
      setAgencyOptions([agencies[0]]);
    }

    let regions = [];
    userRoleInfo.forEach((item) => {
      if (
        item[FieldNames.budgetFields.region] &&
        regions &&
        !regions.includes(item[FieldNames.budgetFields.region])
      ) {
        regions.push(item);
      }
    });
    if (regions && regions.length > 0) {
      setFundingRegionSelection(regions[0]);
      setRegionOptions([regions[0]]);
    }

    let units = [];
    userRoleInfo.forEach((item) => {
      if (
        item[FieldNames.budgetFields.unit] &&
        units &&
        !units.includes(item[FieldNames.budgetFields.unit])
      ) {
        units.push(item);
      }
    });
    if (units && units.length > 0) {
      setFundingUnitSelection(units[0]);
      setUnitOptions([units[0]]);
    }

    let subUnits = [];
    userRoleInfo.forEach((item) => {
      if (
        item[FieldNames.budgetFields.subUnit] &&
        subUnits &&
        !subUnits.includes(item[FieldNames.budgetFields.subUnit])
      ) {
        subUnits.push(item);
      }
    });
    if (subUnits && subUnits.length > 0) {
      setFundingSubUnitSelection(subUnits[0]);
      setSubUnitOptions([subUnits[0]]);
    }

    let tribes = [];
    userRoleInfo.forEach((item) => {
      if (
        item[FieldNames.userRoles.tribe] &&
        tribes &&
        !tribes.includes(item[FieldNames.userRoles.tribe])
      ) {
        tribes.push(item[FieldNames.userRoles.tribe]);
      }
    });
    //We need to use the tribe the user has assigned if they have one
    if (tribes && tribes.length > 0) {
      setFundingTribeSelection(tribes[0]);
      setTribeOptions([tribes[0]]);
    }
  }, [userRoleInfo]);

  useEffect(() => {
    let active = true;

    if (!departmentLoading) {
      return undefined;
    }

    (async () => {
      if (active) {
        UserHelperService.getAllDepartments().then(
          (response) => {
            const withBlank = [{ Department: "N/A" }, ...response];
            const unique = [...new Set(withBlank)];
            setDepartmentOptions(unique);
            if(response && response.length === 1)
            {
              setFundingDepartmentSelection(response[0]);
            }
          }
        ).catch(error => {
          ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
        });
      }
    })();

    return () => {
      active = false;
    };
  }, [departmentLoading, config, rootServiceUrl]);

  useEffect(() => {
    if (!departmentOpen) {
      setDepartmentOptions([]);
    }
  }, [departmentOpen]);

  useEffect(() => {
    let active = true;

    if(!fundingDepartmentSelection || !fundingDepartmentSelection[FieldNames.budgetFields.department])
    {
      return undefined;
    }

    (async () => {
      if (active) {
        agencyCall();
      }
    })();

    return () => {
      active = false;
    };
  }, [agencyLoading, fundingDepartmentSelection, config, rootServiceUrl]);

  useEffect(() => {
    if (!agencyOpen) {
      setAgencyOptions([]);
    }
  }, [agencyOpen]);

  const agencyCall = () => {
    if (fundingDepartmentSelection && fundingDepartmentSelection[FieldNames.budgetFields.department]) {
      const arrayOfDept = [
        fundingDepartmentSelection[FieldNames.budgetFields.department],
      ];
      UserHelperService.getAgencyByDepartments(
        arrayOfDept
      ).then((response) => {
        const withBlank = [{ Agency: "N/A" }, ...response];
        const unique = [...new Set(withBlank)];
        setAgencyOptions(unique);
        if(response && response.length === 1)
        {
          setFundingAgencySelection(response[0]);
        }
      }).catch(error => {
        ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
      });
    } else {
      UserHelperService.getAllAgencies().then(
        (response) => {
          const withBlank = [{ Agency: "N/A" }, ...response];
          const unique = [...new Set(withBlank)];
          setAgencyOptions(unique);
          if(response && response.length === 1)
          {
            setFundingAgencySelection(response[0]);
          }
        }
      ).catch(error => {
        ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
      });
    }
  }

  useEffect(() => {
    let active = true;
    if(setColumnVisibilityModel)
    {
      setColumnVisibilityModel({
        SubUnit: showSubunit,
        Tribe: showTribe,
      });
    }

    if(!fundingAgencySelection || !fundingAgencySelection[FieldNames.budgetFields.agency])
    {
      return undefined;
    }

    (async () => {
      if (active) {
        regionCall();
      }
    })();

    return () => {
      active = false;
    };
  }, [regionLoading, fundingAgencySelection, config, rootServiceUrl]);

  useEffect(() => {
    if (!regionOpen) {
      setRegionOptions([]);
    }
  }, [regionOpen]);

  const regionCall = () =>{
    if (fundingAgencySelection && fundingAgencySelection[FieldNames.budgetFields.agency]) {
      const arrayOfAgency = [
        fundingAgencySelection[FieldNames.budgetFields.agency],
      ];
      UserHelperService.getRegionsByAgencies(
        arrayOfAgency
      ).then((response) => {
        const withBlank = [{ Region: "N/A" }, ...response];
        const unique = [...new Set(withBlank)];
        setRegionOptions(unique);
        if(response && response.length === 1)
        {
          setFundingRegionSelection(response[0]);
        }
      }).catch(error => {
        ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
      });
    } else {
      UserHelperService.getAllRegions().then(
        (response) => {
          const withBlank = [{ Region: "N/A" }, ...response];
          const unique = [...new Set(withBlank)];
          setFundingRegionSelection(unique);
          if(response && response.length === 1)
          {
            setFundingRegionSelection(response[0]);
          }
        }
      ).catch(error => {
        ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
      });
    }
  }

  useEffect(() => {
    let active = true;

    if(!fundingRegionSelection || !fundingRegionSelection[FieldNames.budgetFields.region])
    {
      return undefined;
    }

    (async () => {
      if (active) {
        unitCall();
      }
    })();

    return () => {
      active = false;
    };
  }, [unitLoading, fundingRegionSelection, config, rootServiceUrl]);

  useEffect(() => {
    if (!unitOpen) {
      setUnitOptions([]);
    }
  }, [unitOpen]);

  const unitCall = () => {
    if (fundingRegionSelection && fundingRegionSelection[FieldNames.budgetFields.region]) {
      const regionValues = [
        fundingRegionSelection[FieldNames.budgetFields.region],
      ];
      if (fundingAgencySelection && fundingAgencySelection[FieldNames.budgetFields.agency]) {
        const agencyValues = [
          fundingAgencySelection[FieldNames.budgetFields.agency],
        ];
        UserHelperService.getUnitsByRegionsAgencies(
          regionValues,
          agencyValues
        ).then((response) => {
          const withBlank = [{ Unit: "N/A" }, ...response];
          const unique = [...new Set(withBlank)];
          setUnitOptions(unique);
          if(response && response.length === 1)
          {
            setFundingUnitSelection(response[0]);
          }
        }).catch(error => {
          ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
        });
      } else {
        UserHelperService.getUnitsByRegions(
          regionValues
        ).then((response) => {
          const withBlank = [{ Unit: "N/A" }, ...response];
          const unique = [...new Set(withBlank)];
          setUnitOptions(unique);
          if(response && response.length === 1)
          {
            setFundingUnitSelection(response[0]);
          }
        }).catch(error => {
          ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
        });
      }
    } else {
      UserHelperService.getAllUnits().then(
        (response) => {
          const withBlank = [{ Unit: "N/A" }, ...response];
          const unique = [...new Set(withBlank)];
          setUnitOptions(unique);
          if(response && response.length === 1)
          {
            setFundingUnitSelection(response[0]);
          }
        }
      ).catch(error => {
        ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
      });
    }
  }

  useEffect(() => {
    let active = true;

    if(!fundingUnitSelection || !fundingUnitSelection[FieldNames.budgetFields.unit]  || !fundingAgencySelection
      || !fundingAgencySelection[FieldNames.budgetFields.agency] 
      || fundingAgencySelection[FieldNames.budgetFields.agency] !== FieldNames.agencies.blm)
    {
      return undefined;
    }

    (async () => {
      if (active) {
        subUnitCall();
      }
    })();

    return () => {
      active = false;
    };
  }, [subUnitLoading, fundingUnitSelection, config, rootServiceUrl]);

  useEffect(() => {
    if (!subUnitOpen) {
      setSubUnitOptions([]);
    }
  }, [subUnitOpen]);

  const subUnitCall = () => {
    if (fundingUnitSelection && fundingUnitSelection[FieldNames.budgetFields.unit]) {
      const unitValues = [
        fundingUnitSelection[FieldNames.budgetFields.unit],
      ];
      UserHelperService.getSubUnitsByUnits(
        unitValues
      ).then((response) => {
        const withBlank = [
          { SubUnit: "N/A" },
          { SubUnit: "No SubUnit" },
          ...response,
        ];
        const unique = [...new Set(withBlank)];
        setSubUnitOptions(unique);
        if(response && response.length === 1)
        {
          setFundingSubUnitSelection(response[0]);
        }
      }).catch(error => {
        ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
      });
    } else {
      UserHelperService.getAllSubUnits().then(
        (response) => {
          const withBlank = [
            { SubUnit: "N/A" },
            { SubUnit: "No SubUnit" },
            ...response,
          ];
          const unique = [...new Set(withBlank)];
          setSubUnitOptions(unique);
          if(response && response.length === 1)
          {
            setFundingSubUnitSelection(response[0]);
          }
        }
      ).catch(error => {
        ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
      });
    }
  }

  useEffect(() => {
    let active = true;

    if(!fundingUnitSelection || !fundingUnitSelection[FieldNames.budgetFields.unit]  || !fundingAgencySelection
      || !fundingAgencySelection[FieldNames.budgetFields.agency] 
      || fundingAgencySelection[FieldNames.budgetFields.agency] !== FieldNames.agencies.bia)
    {
      return undefined;
    }

    (async () => {
      if (active) {
        tribeCall();
      }
    })();

    return () => {
      active = false;
    };
  }, [
    tribeLoading,
    fundingUnitSelection,
    config,
    rootServiceUrl,
  ]);

  useEffect(() => {
    if (!tribeOpen) {
      setTribeOptions([]);
    }
  }, [tribeOpen]);

  const tribeCall = () => {
    if (fundingUnitSelection && fundingUnitSelection[FieldNames.budgetFields.unit]) {
      const unitValues = [
        fundingUnitSelection[FieldNames.budgetFields.unit],
      ];
      UserHelperService.getTribesByUnits(
        unitValues
      ).then((response) => {
        const withBlank = [
          { TribeFullName: "N/A" },
          { TribeFullName: "No Tribe" },
          ...response,
        ];
        const unique = [...new Set(withBlank)];
        setTribeOptions(unique);
        if(response && response.length === 1)
        {
          setFundingTribeSelection(response[0]);
        }
      }).catch(error => {
        ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
      });
    } else {
      if (fundingRegionSelection && fundingRegionSelection[FieldNames.units.region]) {
        const regionValues = [
          fundingRegionSelection[FieldNames.units.region],
        ];
        UserHelperService.getTribesByRegions(
          regionValues
        ).then((response) => {
          const withBlank = [
            { TribeFullName: "N/A" },
            { TribeFullName: "No Tribe" },
            ...response,
          ];
          const unique = [...new Set(withBlank)];
          setTribeOptions(unique);
          if(response && response.length === 1)
          {
            setFundingTribeSelection(response[0]);
          }
        }).catch(error => {
          ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
        });
      } else {
        UserHelperService.getAllTribes().then(
          (response) => {
            const withBlank = [
              { TribeFullName: "N/A" },
              { TribeFullName: "No Tribe" },
              ...response,
            ];
            const unique = [...new Set(withBlank)];
            setTribeOptions(unique);
            if(response && response.length === 1)
            {
              setFundingTribeSelection(response[0]);
            }
          }
        ).catch(error => {
          ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
        });
      }
    }
  }

  return (
    <Grid container>
            <FormControl variant="standard" className={classes.formControl}>
              <ThemeProvider theme={theme}>
                {isUserFuelsAndPostFire && 
                  <Grid
                    item
                    size={12}
                    container
                    direction="row"
                    className="grid-item"
                    style={{ alignItems: "center"}}
                  >
                    <RadioGroup
                    aria-labelledby="demo-radio-buttons-group-label"
                    value={entityTypeSelection}
                    sx={{display: 'inline', color: ColorConstants.BlueAlien, marginLeft: 0}}
                    name="radio-buttons-group"
                    onChange={(event) => {
                      if(event.target.value === FieldNames.datasets.fuels){
                        if(setIsTreatment){
                          setIsTreatment(true);
                        }
                        if(setClassOptionSelection){
                          setClassOptionSelection(FieldNames.targetOptions.treatments);
                        }
                      }
                      setEntityTypeSelection(event.target.value)
                    }}
                    >
                      <HeaderFormControlLabel style={{ paddingRight: 10 }} value={FieldNames.datasets.fuels} control={<HeaderRadio />} label={FieldNames.datasets.fuels} />
                      <HeaderFormControlLabel value={FieldNames.datasets.postfire} control={<HeaderRadio />} label={FieldNames.datasets.postfire} />
                    </RadioGroup>
                  </Grid>
                }
                <Grid
                  item
                  size={12}
                  container
                  direction="row"
                  className="grid-item"
                  style={{ alignItems: "center" }}
                >
                  <Grid item size={2}>
                    <span>
                      <label>Department</label>
                    </span>
                  </Grid>
                  <Grid item size={3}>
                    <Autocomplete
                      //multiple
                      disableClearable
                      id="departmentDemo"
                      value={fundingDepartmentSelection}
                      onChange={(event, newValue) => {
                        if (
                          newValue[FieldNames.budgetFields.department] === "N/A"
                        ) {
                          newValue[FieldNames.budgetFields.department] = "";
                        }
                        setFundingDepartmentSelection(newValue);
                        setFundingAgencySelection({Agency: ""});
                        setFundingRegionSelection({ Region: "" });
                        setFundingUnitSelection({ Unit: "" });
                        setFundingSubUnitSelection({ SubUnit: "" });
                        setFundingTribeSelection({ TribeFullName: "" });
                        if(setTotalCount)
                        {
                          setTotalCount(0);
                        }                      
                      }}
                      onOpen={() => {
                        setDepartmentOpen(true);
                      }}
                      onClose={() => {
                        setDepartmentOpen(false);
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option[FieldNames.budgetFields.department] ===
                        value[FieldNames.budgetFields.department]
                      }
                      getOptionLabel={(option) => {
                        return option[FieldNames.budgetFields.department];
                      }}
                      options={departmentOptions}
                      getOptionDisabled={(option) => {
                        if (fundingDepartmentSelection) {
                          const selected =
                            fundingDepartmentSelection[
                              FieldNames.budgetFields.department
                            ];
                          if (
                            selected &&
                            option &&
                            selected.includes(
                              option[FieldNames.budgetFields.department]
                            )
                          ) {
                            return true;
                          }  else if (
                            !selected &&
                            option[FieldNames.budgetFields.agency] === "N/A"
                          ) {
                            return true;
                          } else {
                            return false;
                          }
                        } else {
                          return false;
                        }
                      }}
                      loading={departmentLoading}
                      renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => (
                          <Chip
                            label={option[FieldNames.budgetFields.department]}
                            {...getTagProps({ index })}
                            disabled={false}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          slotProps={{
                            input: {
                              ...params.InputProps,
                              endAdornment: (
                                <React.Fragment>
                                  {departmentLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                  {params?.InputProps?.endAdornment}
                                </React.Fragment>
                              )
                            }
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item size={1} />
                  <Grid item size={2}>
                    <span>
                      <label>Agency</label>
                    </span>
                  </Grid>
                  <Grid item size={3}>
                    <Autocomplete
                      //multiple
                      disableClearable
                      id="agencyDemo"
                      value={fundingAgencySelection}
                      disabled={
                        !fundingDepartmentSelection ||
                        !fundingDepartmentSelection[
                          FieldNames.budgetFields.department
                        ]
                      }
                      onChange={(event, newValue) => {
                        if (
                          newValue[FieldNames.budgetFields.agency] === "N/A"
                        ) {
                          newValue[FieldNames.budgetFields.agency] = "";
                        }
                        setFundingAgencySelection(newValue);
                        setFundingRegionSelection({ Region: "" });
                        setFundingUnitSelection({ Unit: "" });
                        setFundingSubUnitSelection({ SubUnit: "" });
                        setFundingTribeSelection({ TribeFullName: "" });
                        if(setTotalCount)
                        {
                          setTotalCount(0);
                        }                      
                      }}
                      onOpen={() => {
                        setAgencyOpen(true);
                      }}
                      onClose={() => {
                        setAgencyOpen(false);
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option[FieldNames.budgetFields.agency] ===
                          value[FieldNames.budgetFields.agency] ||
                        value[FieldNames.budgetFields.agency] === ""
                      }
                      getOptionLabel={(option) => {
                        return option[FieldNames.budgetFields.agency];
                      }}
                      options={agencyOptions}
                      getOptionDisabled={(option) => {
                        if (fundingAgencySelection) {
                          const selected =
                            fundingAgencySelection[
                              FieldNames.budgetFields.agency
                            ];
                          if (
                            selected &&
                            option &&
                            selected.includes(
                              option[FieldNames.budgetFields.agency]
                            )
                          ) {
                            return true;
                          } else if (
                            !selected &&
                            option[FieldNames.budgetFields.agency] === "N/A"
                          ) {
                            return true;
                          } else {
                            return false;
                          }
                        } else {
                          return false;
                        }
                      }}
                      loading={agencyLoading}
                      renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => (
                          <Chip
                            label={option[FieldNames.budgetFields.agency]}
                            {...getTagProps({ index })}
                            disabled={false}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          slotProps={{
                            input: {
                              ...params.InputProps,
                              endAdornment: (
                                <React.Fragment>
                                  {agencyLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                  {params?.InputProps?.endAdornment}
                                </React.Fragment>
                              )
                            }
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </ThemeProvider>
            </FormControl>
            <FormControl variant="standard" className={classes.formControl}>
              <ThemeProvider theme={theme}>
                <Grid
                  item
                  size={12}
                  
                  container
                  direction="row"
                  className="grid-item"
                  style={{ alignItems: "center" }}
                >
                  <Grid item size={2}>
                    <span>
                      <label>Region</label>
                    </span>
                  </Grid>
                  <Grid item size={3}>
                    <Autocomplete
                      //multiple
                      disableClearable
                      id="regionMulti"
                      open={regionOpen}
                      value={fundingRegionSelection}
                      disabled={
                        !fundingAgencySelection ||
                        !fundingAgencySelection[FieldNames.budgetFields.agency]
                      }
                      onChange={(event, newValue) => {
                        if (
                          newValue[FieldNames.budgetFields.region] === "N/A"
                        ) {
                          newValue[FieldNames.budgetFields.region] = "";
                        }
                        setFundingRegionSelection(newValue);
                        setFundingUnitSelection({ Unit: "" });
                        setFundingSubUnitSelection({ SubUnit: "" });
                        setFundingTribeSelection({ TribeFullName: "" });
                        if(setTotalCount)
                        {
                          setTotalCount(0);
                        }                      
                      }}
                      onOpen={() => {
                        setRegionOpen(true);
                      }}
                      onClose={() => {
                        setRegionOpen(false);
                      }}
                      isOptionEqualToValue={(option, value) => {
                        return (
                          option[FieldNames.budgetFields.region] ===
                            value[FieldNames.budgetFields.region] ||
                          value[FieldNames.budgetFields.region] === ""
                        );
                      }}
                      getOptionLabel={(option) => {
                        return option[FieldNames.budgetFields.region];
                      }}
                      options={regionOptions}
                      getOptionDisabled={(option) => {
                        if (fundingRegionSelection) {
                          const selected =
                            fundingRegionSelection[
                              FieldNames.budgetFields.region
                            ];
                          if (
                            selected &&
                            option &&
                            selected.includes(
                              option[FieldNames.budgetFields.region]
                            )
                          ) {
                            return true;
                          } else if (
                            !selected &&
                            option[FieldNames.budgetFields.region] === "N/A"
                          ) {
                            return true;
                          } else {
                            return false;
                          }
                        } else {
                          return false;
                        }
                      }}
                      loading={regionLoading}
                      renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => (
                          <Chip
                            label={option[FieldNames.budgetFields.region]}
                            {...getTagProps({ index })}
                            disabled={false}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          slotProps={{
                            input: {
                              ...params.InputProps,
                              endAdornment: (
                                <React.Fragment>
                                  {regionLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                  {params?.InputProps?.endAdornment}
                                </React.Fragment>
                              )
                            }
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item size={1} />
                  <Grid item size={2}>
                    <span>
                      <label>Unit</label>
                    </span>
                  </Grid>
                  <Grid item size={3}>
                    <Autocomplete
                      //multiple
                      disableClearable
                      id="unitMulti"
                      open={unitOpen}
                      value={fundingUnitSelection}
                      disabled={
                        !fundingRegionSelection ||
                        !fundingRegionSelection[FieldNames.budgetFields.region]
                      }
                      onChange={(event, newValue) => {
                        if (newValue[FieldNames.budgetFields.unit] === "N/A") {
                          newValue[FieldNames.budgetFields.unit] = "";
                        }
                        setFundingUnitSelection(newValue);
                        setFundingSubUnitSelection({ SubUnit: "" });
                        setFundingTribeSelection({ TribeFullName: "" });
                        if(setTotalCount)
                        {
                          setTotalCount(0);
                        }                      
                      }}
                      onOpen={() => {
                        setUnitOpen(true);
                      }}
                      onClose={() => {
                        setUnitOpen(false);
                      }}
                      isOptionEqualToValue={(option, value) => {
                        return (
                          option[FieldNames.budgetFields.unit] ===
                            value[FieldNames.budgetFields.unit] ||
                          value[FieldNames.budgetFields.unit] === ""
                        );
                      }}
                      getOptionLabel={(option) => {
                        return option[FieldNames.budgetFields.unit];
                      }}
                      options={unitOptions}
                      getOptionDisabled={(option) => {
                        if (fundingUnitSelection) {
                          const selected =
                            fundingUnitSelection[FieldNames.budgetFields.unit];
                          if (
                            selected &&
                            option &&
                            selected.includes(
                              option[FieldNames.budgetFields.unit]
                            )
                          ) {
                            return true;
                          } else if (!selected && option.unit === "N/A") {
                            return true;
                          } else {
                            return false;
                          }
                        } else {
                          return false;
                        }
                      }}
                      loading={unitLoading}
                      renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => (
                          <Chip
                            label={option[FieldNames.budgetFields.unit]}
                            {...getTagProps({ index })}
                            disabled={false}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          slotProps={{
                            input: {
                              ...params.InputProps,
                              endAdornment: (
                                <React.Fragment>
                                  {unitLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                  {params?.InputProps?.endAdornment}
                                </React.Fragment>
                              )
                            }
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </ThemeProvider>
            </FormControl>
            <FormControl variant="standard" className={classes.formControl}>
              <ThemeProvider theme={theme}>
                <Grid
                  item
                  size={12}
                  
                  container
                  direction="row"
                  className="grid-item"
                  style={{ alignItems: "center" }}
                >
                  {showSubunit ? (
                    <>
                      <Grid item size={2} className={showSubunit ? "" : "Hidden"}>
                        <span>
                          <label>SubUnit</label>
                        </span>
                      </Grid>
                      <Grid item size={3} className={showSubunit ? "" : "Hidden"}>
                        <Autocomplete
                          //multiple
                          disableClearable
                          id="unitMulti"
                          open={subUnitOpen}
                          value={fundingSubUnitSelection}
                          disabled={
                            !fundingUnitSelection ||
                            !fundingUnitSelection[
                              FieldNames.budgetFields.unit
                            ] ||
                            !fundingAgencySelection ||
                            fundingAgencySelection[
                              FieldNames.budgetFields.agency
                            ] !== "BLM"
                          }
                          onChange={(event, newValue) => {
                            if (
                              newValue[FieldNames.budgetFields.subUnit] ===
                              "N/A"
                            ) {
                              newValue[FieldNames.budgetFields.subUnit] = "";
                            }
                            setFundingSubUnitSelection(newValue);
                            if(setTotalCount)
                            {
                              setTotalCount(0);
                            }
                          }}
                          onOpen={() => {
                            setSubUnitOpen(true);
                          }}
                          onClose={() => {
                            setSubUnitOpen(false);
                          }}
                          isOptionEqualToValue={(option, value) => {
                            return (
                              option[FieldNames.budgetFields.subUnit] ===
                                value[FieldNames.budgetFields.subUnit] ||
                              value[FieldNames.budgetFields.subUnit] === ""
                            );
                          }}
                          getOptionLabel={(option) => {
                            return option[FieldNames.budgetFields.subUnit];
                          }}
                          options={subUnitOptions}
                          getOptionDisabled={(option) => {
                            if (fundingSubUnitSelection) {
                              const selected =
                                fundingSubUnitSelection[
                                  FieldNames.budgetFields.subUnit
                                ];
                              if (
                                selected &&
                                option &&
                                selected.includes(
                                  option[FieldNames.budgetFields.subUnit]
                                )
                              ) {
                                return true;
                              } else if (
                                !selected &&
                                option[FieldNames.budgetFields.subUnit] ===
                                  "N/A"
                              ) {
                                return true;
                              } else {
                                return false;
                              }
                            } else {
                              return false;
                            }
                          }}
                          loading={subUnitLoading}
                          renderTags={(tagValue, getTagProps) =>
                            tagValue.map((option, index) => (
                              <Chip
                                label={option[FieldNames.budgetFields.subUnit]}
                                {...getTagProps({ index })}
                                disabled={false}
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              slotProps={{
                                input: {
                                  ...params.InputProps,
                                  endAdornment: (
                                    <React.Fragment>
                                      {unitLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                      {params?.InputProps?.endAdornment}
                                    </React.Fragment>
                                  )
                                }
                              }}
                            />
                          )}
                        />
                      </Grid>
                    </>
                  ) : null}
                  {showSubunit && showTribe ? <Grid item size={1} /> : null}
                  {showTribe ? (
                    <>
                      <Grid item size={2} className={showTribe ? "" : "Hidden"}>
                        <span>
                          <label>Tribe</label>
                        </span>
                      </Grid>
                      <Grid item size={3}>
                        <Autocomplete
                          //multiple
                          disableClearable
                          id="tribeMulti"
                          open={tribeOpen}
                          value={fundingTribeSelection}
                          disabled={
                            !fundingUnitSelection ||
                            !fundingUnitSelection[
                              FieldNames.budgetFields.unit
                            ] ||
                            !fundingAgencySelection ||
                            fundingAgencySelection[
                              FieldNames.budgetFields.agency
                            ] !== "BIA"
                          }
                          onChange={(event, newValue) => {
                            if (
                              newValue[FieldNames.units.tribeFullName] === "N/A"
                            ) {
                              newValue[FieldNames.units.tribeFullName] = "";
                            }
                            setFundingTribeSelection(newValue);
                            if(setTotalCount)
                            {
                              setTotalCount(0);
                            }                      
                          }}
                          onOpen={() => {
                            setTribeOpen(true);
                          }}
                          onClose={() => {
                            setTribeOpen(false);
                          }}
                          isOptionEqualToValue={(option, value) => {
                            return (
                              option[FieldNames.units.tribeFullName] ===
                                value[FieldNames.units.tribeFullName] ||
                              value[FieldNames.units.tribeFullName] === ""
                            );
                          }}
                          getOptionLabel={(option) => {
                            return option[FieldNames.units.tribeFullName];
                          }}
                          options={tribeOptions}
                          getOptionDisabled={(option) => {
                            if (fundingTribeSelection) {
                              const selected =
                                fundingTribeSelection[
                                  FieldNames.units.tribeFullName
                                ];
                              if (
                                selected &&
                                option &&
                                selected.includes(
                                  option[FieldNames.units.tribeFullName]
                                )
                              ) {
                                return true;
                              } else if (
                                !selected &&
                                option[FieldNames.units.tribeFullName] === "N/A"
                              ) {
                                return true;
                              } else {
                                return false;
                              }
                            } else {
                              return false;
                            }
                          }}
                          loading={tribeLoading}
                          renderTags={(tagValue, getTagProps) =>
                            tagValue.map((option, index) => (
                              <Chip
                                label={option[FieldNames.units.tribeFullName]}
                                {...getTagProps({ index })}
                                disabled={false}
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              slotProps={{
                                input: {
                                  ...params.InputProps,
                                  endAdornment: (
                                    <React.Fragment>
                                      {tribeLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                      {params?.InputProps?.endAdornment}
                                    </React.Fragment>
                                  )
                                }
                              }}
                            />
                          )}
                        />
                      </Grid>
                    </>
                  ) : null}
                </Grid>
              </ThemeProvider>
            </FormControl>
          </Grid>
  );
}